/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */

import { useState, useCallback } from 'react'
import { useAppDispatch } from 'helpers/store'
import LeadsAction from 'store/leads/LeadsAction'
import { CompaniesAction } from 'store/companies/CompaniesAction'
import { Lead } from 'types/entities/Lead'
import { Company } from 'types/entities/Company'

export const useOfferLinkedEntities = (leadId?: number | null, companyId?: number | null) => {
    const dispatch = useAppDispatch()

    const [loadedLeadState, setLoadedLeadState] = useState<{ leadId: number | null; lead: Lead | null }>({
        leadId: null,
        lead: null,
    })
    const [loadedCompanyState, setLoadedCompanyState] = useState<{
        companyId: number | null
        company: Company | null
    }>({
        companyId: null,
        company: null,
    })

    const loadedLead =
        loadedLeadState.leadId && loadedLeadState.leadId === leadId ? loadedLeadState.lead : null

    const loadedCompany =
        loadedCompanyState.companyId && loadedCompanyState.companyId === companyId
            ? loadedCompanyState.company
            : null

    const loadLead = useCallback(() => {
        if (!leadId) return
        if (loadedLead && loadedLead.id === leadId) return
        dispatch(LeadsAction.fetchLeadById({ id: leadId } as Lead)).then((res) => {
            setLoadedLeadState({ leadId, lead: res })
        })
    }, [dispatch, leadId, loadedLead])

    const loadCompany = useCallback(() => {
        if (!companyId) return
        if (loadedCompany && loadedCompany.id === companyId) return
        dispatch(CompaniesAction.fetchCompanyById(companyId)).then((res) => {
            setLoadedCompanyState({ companyId, company: res })
        })
    }, [dispatch, companyId, loadedCompany])

    return { loadedLead, loadedCompany, loadLead, loadCompany }
}
